/**
 * TutorialContext - Global state management for the tutorial/onboarding system.
 * Follows the same pattern as ThemeContext.
 */

import React, { createContext, useState, useContext, useCallback, useMemo } from 'react';

export const TutorialContext = createContext();

export const TutorialProvider = ({ children }) => {
  // State
  const [isActive, setIsActive] = useState(false);
  const [currentTutorial, setCurrentTutorial] = useState(null);
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [targetElement, setTargetElement] = useState(null);
  const [isWaitingForElement, setIsWaitingForElement] = useState(false);

  // Start a tutorial
  const startTutorial = useCallback((tutorial) => {
    if (!tutorial || !tutorial.steps || tutorial.steps.length === 0) {
      console.error('Invalid tutorial provided');
      return;
    }
    setCurrentTutorial(tutorial);
    setCurrentStepIndex(0);
    setIsActive(true);
    setTargetElement(null);
    setIsWaitingForElement(false);
  }, []);

  // End the tutorial (complete or skip)
  const endTutorial = useCallback((completed = true) => {
    // Mark as completed in localStorage if finished
    if (completed && currentTutorial?.id) {
      const completedTutorials = JSON.parse(localStorage.getItem('completed-tutorials') || '[]');
      if (!completedTutorials.includes(currentTutorial.id)) {
        completedTutorials.push(currentTutorial.id);
        localStorage.setItem('completed-tutorials', JSON.stringify(completedTutorials));
      }
    }

    setIsActive(false);
    setCurrentTutorial(null);
    setCurrentStepIndex(0);
    setTargetElement(null);
    setIsWaitingForElement(false);
  }, [currentTutorial]);

  // Go to next step
  const nextStep = useCallback(() => {
    if (!currentTutorial) return;

    if (currentStepIndex < currentTutorial.steps.length - 1) {
      setCurrentStepIndex(prev => prev + 1);
      setTargetElement(null);
      setIsWaitingForElement(false);
    } else {
      // Last step - complete the tutorial
      endTutorial(true);
    }
  }, [currentTutorial, currentStepIndex, endTutorial]);

  // Go to previous step
  const prevStep = useCallback(() => {
    if (currentStepIndex > 0) {
      setCurrentStepIndex(prev => prev - 1);
      setTargetElement(null);
      setIsWaitingForElement(false);
    }
  }, [currentStepIndex]);

  // Skip/exit tutorial without completing
  const skipTutorial = useCallback(() => {
    endTutorial(false);
  }, [endTutorial]);

  // Check if a tutorial has been completed
  const isTutorialCompleted = useCallback((tutorialId) => {
    const completedTutorials = JSON.parse(localStorage.getItem('completed-tutorials') || '[]');
    return completedTutorials.includes(tutorialId);
  }, []);

  // Reset a tutorial's completion status
  const resetTutorial = useCallback((tutorialId) => {
    const completedTutorials = JSON.parse(localStorage.getItem('completed-tutorials') || '[]');
    const filtered = completedTutorials.filter(id => id !== tutorialId);
    localStorage.setItem('completed-tutorials', JSON.stringify(filtered));
  }, []);

  // Current step helper
  const currentStep = useMemo(() =>
    currentTutorial?.steps?.[currentStepIndex] || null,
    [currentTutorial, currentStepIndex]
  );

  // Total steps helper
  const totalSteps = useMemo(() =>
    currentTutorial?.steps?.length || 0,
    [currentTutorial]
  );

  // Memoize context value
  const value = useMemo(() => ({
    // State
    isActive,
    currentTutorial,
    currentStep,
    currentStepIndex,
    totalSteps,
    targetElement,
    isWaitingForElement,
    // Methods
    startTutorial,
    endTutorial,
    nextStep,
    prevStep,
    skipTutorial,
    setTargetElement,
    setIsWaitingForElement,
    isTutorialCompleted,
    resetTutorial
  }), [
    isActive, currentTutorial, currentStep, currentStepIndex, totalSteps,
    targetElement, isWaitingForElement, startTutorial, endTutorial, nextStep,
    prevStep, skipTutorial, isTutorialCompleted, resetTutorial
  ]);

  return (
    <TutorialContext.Provider value={value}>
      {children}
    </TutorialContext.Provider>
  );
};

// Custom hook for accessing tutorial context
export const useTutorial = () => {
  const context = useContext(TutorialContext);
  if (!context) {
    throw new Error('useTutorial must be used within a TutorialProvider');
  }
  return context;
};
